'use client';

import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import type { Checklist } from '@church/nextjs-ui/website';

/**
 * A LIST YOU CAN TICK OFF.
 *
 * Staff keep the list in MP (Checklists + Checklist_Items) and attach it to an
 * opportunity or a care offer, so the same seven Thanksgiving items are the
 * shopping list for packing a box at home, the inventory sheet for checking
 * boxes, and what a family receives. Laid out like the event agenda: a heading
 * over one card of rows.
 *
 * Ticks are the visitor's own, remembered on this device per page (`scope`),
 * so packing two boxes on two pages never shares progress. Nothing is sent
 * anywhere.
 */

const KICKER =
  'font-[family-name:var(--font-display)] text-[15px] leading-none tracking-[0.06em] uppercase';

function useTicks(key: string) {
  const [ticked, setTicked] = useState<Set<number>>(() => new Set());
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) setTicked(new Set(JSON.parse(raw) as number[]));
    } catch {
      /* private window or blocked storage: ticks just do not persist */
    }
  }, [key]);
  const toggle = (id: number) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(key, JSON.stringify([...next]));
      } catch {
        /* see above */
      }
      return next;
    });
  return [ticked, toggle] as const;
}

export function ChecklistCard({ checklist, scope }: { checklist: Checklist; scope: string }) {
  const [ticked, toggle] = useTicks(`sf-checklist:${scope}:${checklist.id}`);
  if (!checklist.items.length) return null;
  const done = checklist.items.filter((i) => ticked.has(i.id)).length;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className={`${KICKER} text-gray-500`}>{checklist.heading ?? 'Checklist'}</h2>
        {done > 0 && (
          <span className="text-xs font-semibold text-gray-500">
            {done} of {checklist.items.length}
          </span>
        )}
      </div>
      {checklist.description && (
        <p className="-mt-1 text-sm text-gray-600">{checklist.description}</p>
      )}
      <ul className="divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white shadow-sm">
        {checklist.items.map((it) => {
          const on = ticked.has(it.id);
          return (
            <li key={it.id}>
              <button
                type="button"
                onClick={() => toggle(it.id)}
                aria-pressed={on}
                className="flex w-full items-start gap-3 px-4 py-3 text-left transition active:scale-[0.99]"
              >
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                    on
                      ? 'border-[var(--accent,#299c8f)] bg-[var(--accent,#299c8f)] text-[var(--on-accent,#fff)]'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block text-sm font-bold transition-colors ${
                      on ? 'text-gray-400 line-through' : 'text-[#1c2b39]'
                    }`}
                  >
                    {it.item}
                  </span>
                  {it.detail && (
                    <span className={`block text-xs ${on ? 'text-gray-400' : 'text-gray-500'}`}>
                      {it.detail}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
